export enum LogLevel {
  NONE = 0,
  ERROR = 1,
  WARN = 2,
  INFO = 3,
  DEBUG = 4,
}

export class Logger {
  private static level: LogLevel = Number(process.env['NEXT_PUBLIC_LOG_LEVEL']) || LogLevel.NONE;

  private static readonly COLORS = {
    DEBUG: '#6c757d',
    INFO: '#0d6efd',
    WARN: '#ffc107',
    ERROR: '#dc3545',
  };

  private static shouldLog(messageLevel: LogLevel): boolean {
    return messageLevel <= Logger.level;
  }

  private static getFileInfo(): string {
    try {
      const err = new Error();
      const stack = err.stack?.split('\n')[3]; // Skip 3 lines to get to the caller
      const match = stack?.match(/\((.*):\d+:\d+\)/);
      if (match) {
        const fullPath = match[1];
        const fileName = fullPath.split('/').pop() ?? '';
        return `[${fileName}]`;
      }
    } catch (e) {
      return '';
    }
    return '';
  }

  static startGroup(label: string): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.group(label);
    }
  }

  static endGroup(): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.groupEnd();
    }
  }

  static debug(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      const fileInfo = this.getFileInfo();
      console.debug(
        `%c[DEBUG]%c ${fileInfo} ${message}`,
        `background: ${this.COLORS.DEBUG}; color: white; font-weight: bold; padding: 2px 4px; border-radius: 3px;`,
        '',
        ...args
      );
    }
  }

  static info(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      const fileInfo = this.getFileInfo();
      console.info(
        `%c[INFO]%c ${fileInfo} ${message}`,
        `background: ${this.COLORS.INFO}; color: white; font-weight: bold; padding: 2px 4px; border-radius: 3px;`,
        '',
        ...args
      );
    }
  }

  static warn(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      const fileInfo = this.getFileInfo();
      console.warn(
        `%c[WARN]%c ${fileInfo} ${message}`,
        `background: ${this.COLORS.WARN}; color: black; font-weight: bold; padding: 2px 4px; border-radius: 3px;`,
        '',
        ...args
      );
    }
  }

  static error(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const fileInfo = this.getFileInfo();
      console.error(
        `%c[ERROR]%c ${fileInfo} ${message}`,
        `background: ${this.COLORS.ERROR}; color: white; font-weight: bold; padding: 2px 4px; border-radius: 3px;`,
        '',
        ...args
      );
    }
  }

  static setLevel(level: LogLevel): void {
    Logger.level = level;
  }
}
